// 9fbef606107a605d69c0edbcd8029e5d
import { put } from 'redux-saga/effects';
import has from 'lodash/has';
import applyCouponQuery from '@tcp/core/src/queries/applyCoupon.query.graphql';
import applyMultipleCouponQuery from '@tcp/core/src/queries/applyMultipleCoupon.query.graphql';
import applyBonusPointDay from '@tcp/core/src/queries/applyBonusPointDay.query.graphql';
import applyLoyaltyBenefits from '@tcp/core/src/queries/applyLoyaltyBenefits.query.graphql';
import removeLoyaltyBenefits from '@tcp/core/src/queries/removeLoyaltyBenefits.query.graphql';
import removeCouponOrPromoQuery from '@tcp/core/src/queries/deleteCoupon.query.graphql';
import removeBonusPointDay from '@tcp/core/src/queries/removeBonusPointDay.query.graphql';
import getPromotionDetailsQuery from '@tcp/core/src/queries/getPromotionDetails.query.graphql';
import { checkCache, getDateObject, readCookie } from '@tcp/core/src/utils';
import logger from '@tcp/core/src/utils/loggerInstance';
import isEmpty from 'lodash/isEmpty';
import { SubmissionError } from 'redux-form'; // ES6
import CheckoutConstants from '@tcp/core/src/components/features/CnC/Checkout/Checkout.constants';
import getCartCachedQuery from '@tcp/core/src/queries/getCartCached.query.graphql';
import getBonusPoints from '@tcp/core/src/queries/getBonusPoints.query.graphql';
import getLoyaltyBenefits from '@tcp/core/src/queries/getLoyaltyBenefits.query.graphql';
import { setLoyaltyOrderItems } from '@tcp/core/src/components/features/CnC/AddedToBag/container/AddedToBag.actions';
import {
  FREE_GIFT_WRAPPING,
  IS_APPLIED_TO_BASKET,
  IS_ON_HOLD,
  BONUS_POINT,
} from '@tcp/core/src/components/features/CnC/common/organism/CouponAndPromos/Coupon.constants.js';
import { executeStatefulGraphQLQuery } from '../../handler/handler';
import {
  getFormattedError,
  getDynamicCodeErrorMessage,
  getFreeShippingMPRErrorMessage,
  getApplyCouponLoyaltyErrorMessage,
} from '../../../utils/errorMessage.util';
import { constructCouponStructure, COUPON_STATUS } from '../CnC/CartItemTile';

export const setPlaceCashError = ({ err: errorObj }) => {
  const err = errorObj;
  let params = err[0] && err[0].errorParameters;
  params = params && Object.values(params);
  const isPromoNotAvailable =
    err[0].errorCode === CheckoutConstants.ERR_PROMOTION_NOT_AVAILABLE_AT_THIS_TIME;
  const isPlaceCashError =
    params &&
    (params[0] === CheckoutConstants.PLACE_CASH || (isPromoNotAvailable && params[0] === 'PLCC'));

  if (isPlaceCashError && !isPromoNotAvailable) {
    err[0].errorCode = CheckoutConstants.PLACE_CASH_ERROR;
  }
  return isPlaceCashError;
};

export const updateQueryForBonusPoints = (cache, data) => {
  let couponResp = {};
  if (data && (data.removeBonusDayFromBasket || data.applyBonusDayToBasket)) {
    couponResp = data.removeBonusDayFromBasket || data.applyBonusDayToBasket;
  }
  const { c_isBPDAppliedToBasket: isBPDAppliedToBasket = false } = couponResp;
  const isBonudDayCacheExist = checkCache(cache, 'getBonusDay');
  if (isBonudDayCacheExist) {
    const storedData = cache.readQuery({ query: getBonusPoints });
    const updateData = { ...storedData.getBonusDay, isBPDAppliedToBasket };
    cache.writeQuery({
      query: getBonusPoints,
      data: { getBonusDay: updateData },
    });
  }
};

export const hasGiftOptionWithPromotion = function hasGiftOptionWithPromotion(items) {
  return items.some((item) => {
    if (item?.c_giftOption) {
      return (
        item?.priceAdjustments &&
        item?.priceAdjustments.some(
          (adjustment) => adjustment?.promotionId === 'StandardGiftServiceDiscount'
        )
      );
    }
    return false;
  });
};

export const updateKeyWithValue = function updateKeyWithValue(benefit, type, value, keyName) {
  const updatedBenefits = benefit.benefits.map((item) =>
    item.type === type ? { ...item, [keyName]: value } : item
  );
  return { ...benefit, benefits: updatedBenefits };
};

export const updateQueryForLoyaltyBenefits = (cache, data) => {
  let couponResp = {};
  if (data?.applyLoyaltyBenefits) {
    couponResp = data?.applyLoyaltyBenefits;
  }
  const benefits = JSON.parse(couponResp.benefits || '{}');
  const isBonusApplied = benefits.BonusPoint;
  const isGiftwrapperApplied = benefits.FreeGiftWrap;
  const isOnHoldFreeGiftWrapping = hasGiftOptionWithPromotion(couponResp.productItems);
  const isBonudDayCacheExist = checkCache(cache, 'getLoyaltyBenefits');
  if (isBonudDayCacheExist) {
    const storedData = cache.readQuery({ query: getLoyaltyBenefits });
    let updateData = storedData.getLoyaltyBenefits;
    if (isBonusApplied) {
      updateData = updateKeyWithValue(updateData, BONUS_POINT, true, IS_APPLIED_TO_BASKET);
    }
    if (isGiftwrapperApplied) {
      if (!isOnHoldFreeGiftWrapping) {
        updateData = updateKeyWithValue(updateData, FREE_GIFT_WRAPPING, true, IS_ON_HOLD);
      }
      updateData = updateKeyWithValue(updateData, FREE_GIFT_WRAPPING, true, IS_APPLIED_TO_BASKET);
    }
    cache.writeQuery({
      query: getLoyaltyBenefits,
      data: { getLoyaltyBenefits: updateData },
    });
  }
};

export const updateQueryForRemoveLoyaltyBenefits = (cache, data) => {
  let couponResp = {};
  couponResp = data?.removeLoyaltyBenefits;
  const benefits = JSON.parse(couponResp?.benefits);
  const isAllRemoved = benefits === null;
  const isBonusRemoved = benefits?.FreeGiftWrap;
  const isGiftwrapperRemoved = benefits?.BonusPoint;
  const isBonudDayCacheExist = checkCache(cache, 'getLoyaltyBenefits');
  if (isBonudDayCacheExist) {
    const storedData = cache.readQuery({ query: getLoyaltyBenefits });
    let updateData = storedData.getLoyaltyBenefits;
    if (isAllRemoved) {
      updateData = updateKeyWithValue(updateData, BONUS_POINT, false, IS_APPLIED_TO_BASKET);
      updateData = updateKeyWithValue(updateData, FREE_GIFT_WRAPPING, false, IS_APPLIED_TO_BASKET);
      updateData = updateKeyWithValue(updateData, FREE_GIFT_WRAPPING, false, IS_ON_HOLD);
    } else if (isBonusRemoved) {
      updateData = updateKeyWithValue(updateData, BONUS_POINT, false, IS_APPLIED_TO_BASKET);
    } else if (isGiftwrapperRemoved) {
      updateData = updateKeyWithValue(updateData, FREE_GIFT_WRAPPING, false, IS_ON_HOLD);
      updateData = updateKeyWithValue(updateData, FREE_GIFT_WRAPPING, false, IS_APPLIED_TO_BASKET);
    }
    cache.writeQuery({
      query: getLoyaltyBenefits,
      data: { getLoyaltyBenefits: updateData },
    });
  }
};

const isThresholdError = (code) =>
  code === CheckoutConstants.TCP_PROMOTION_LOYALTY_THRESHOLD_NOT_MET ||
  code === CheckoutConstants.TCP_PROMOTION_PLACECASH_THRESHOLD_NOT_MET;

const getDynamPlaceCashThreshholdNotMetMessage = (err, errorsMapping, missingAmount) => {
  const errorMessageTemplate =
    errorsMapping[CheckoutConstants.TCP_PROMOTION_PLACECASH_THRESHOLD_NOT_MET];
  return errorMessageTemplate.replace('<CODE_PLACEHOLDER>', missingAmount);
};

const getErrorMessage = (couponErr, err, errorsMapping, missingAmount, defaultError) => {
  if (couponErr?.errorCode === CheckoutConstants.TCP_PROMOTION_PLACECASH_THRESHOLD_NOT_MET) {
    return getDynamPlaceCashThreshholdNotMetMessage(err, errorsMapping, missingAmount);
  }
  return defaultError;
};
export const errorHandler = (err, errorsMapping, couponCode) => {
  let errCouponCode = couponCode;
  const isPlaceCashError = setPlaceCashError({ err });
  const couponError = [...err];
  const couponErr = couponError.length && couponError[0];
  let missingAmount = 0;
  let freeShippingMPRError = false;
  let loyaltyError = false;
  if (couponErr && couponErr.errorParameters) {
    missingAmount = couponErr.errorParameters.amountMissing;
    const params = couponErr.errorParameters;
    const paramArray = params && Object.keys(params);
    couponErr.errorParameters = paramArray.toString();
    if (params && Object.values(params)[0] === 'FREESHIPPING_MPR') {
      freeShippingMPRError = true;
    }
    if (params && Object.values(params)[0] === 'LOYALTY') {
      loyaltyError = true;
    }
  }
  const formattedErrorReq = { errorResponse: couponErr };
  const error = getFormattedError(formattedErrorReq, errorsMapping, true);
  if (isThresholdError(couponErr?.errorCode)) {
    errCouponCode = missingAmount;
  }
  getDynamicCodeErrorMessage(error, errCouponCode);
  if (freeShippingMPRError) {
    getFreeShippingMPRErrorMessage(error, couponErr);
  }
  if (loyaltyError) {
    getApplyCouponLoyaltyErrorMessage(error, couponErr, errorsMapping);
  }
  error.errorMessages = error.errorMessages || {
    _error: CheckoutConstants.SOMETHING_WENT_WRONG,
  };
  const { errorMessages: { _error = '' } = {} } = error;
  let { errorMessages } = error;

  errorMessages = {
    ...errorMessages,
    errorCode: couponErr?.errorCode || '',
    _error: {
      msg: getErrorMessage(couponErr, err, errorsMapping, missingAmount, _error),
      isPlaceCashError,
    },
  };

  throw new SubmissionError(errorMessages);
};
// eslint-disable-next-line complexity
export const updateQuery = (cache, data) => {
  let couponResp = {};

  if ((data && data.applyCouponToBasket) || data?.applyMultipleCoupons) {
    couponResp = data.applyCouponToBasket || data.applyMultipleCoupons;
  }
  const { c_couponMap: couponMap, ...dataToUpdate } = couponResp;
  let isCacheExist = false;
  if (!isEmpty(dataToUpdate)) {
    isCacheExist = checkCache(cache);
  }
  if (isCacheExist && dataToUpdate) {
    cache.writeQuery({
      query: getCartCachedQuery,
      data: { getCart: dataToUpdate },
      // broadcast: false,
    });
  }
};

export const applyMultipleCouponsToBasket = (couponCode, errorsMapping) => {
  const query = applyMultipleCouponQuery;
  const variables = {
    multipleCouponsRequest: { coupons: [{ codes: couponCode, couponType: 'PLACECASH' }] },
    fromCartPage: false,
  };

  return executeStatefulGraphQLQuery({
    query,
    variables,
    type: 'mutation',
    updateQuery,
  })
    .then((res) => {
      if (res.errors) {
        const err = res.errors;
        errorHandler(err, errorsMapping);
      }
      return res.data && res.data.applyMultipleCoupons;
    })
    .catch((err) => {
      errorHandler(err, errorsMapping);
    });
};

const getQueryAndVariablesForApplyCouponToBasket = (
  couponCode,
  basketId,
  isLoyaltyTieringEnabled,
  fromBonusPointsDay
) => {
  if (isLoyaltyTieringEnabled && fromBonusPointsDay) {
    return {
      query: applyLoyaltyBenefits,
      variables: {
        basketId,
        loyaltyBenefitsRequest: {
          benefits: [{ benefitType: couponCode }],
        },
      },
    };
  }
  if (fromBonusPointsDay) {
    return {
      query: applyBonusPointDay,
      variables: {},
    };
  }
  return {
    query: applyCouponQuery,
    variables: {
      couponDto: {
        code: couponCode,
      },
    },
  };
};

const getUpdateQueryFunction = (isLoyaltyTieringEnabled, fromBonusPointsDay) => {
  if (fromBonusPointsDay) {
    if (isLoyaltyTieringEnabled) return updateQueryForLoyaltyBenefits;
    return updateQueryForBonusPoints;
  }
  return updateQuery;
};

export const applyCouponToBasket = (
  { couponCode = '' },
  errorsMapping,
  cartItemsCount,
  fromCartPage,
  fromBonusPointsDay,
  isCouponOnHoldEmptyCartEnabled,
  isLoyaltyTieringEnabled
) => {
  if (!cartItemsCount && !isCouponOnHoldEmptyCartEnabled) {
    throw new SubmissionError({
      _error: {
        msg: errorsMapping.TCP_PROMOTION_COUPON_ON_EMPTY_CART,
      },
    });
  }
  if (!cartItemsCount && fromBonusPointsDay && isLoyaltyTieringEnabled) {
    throw new SubmissionError({
      _error: {
        msg: errorsMapping.TCP_BENEFITS_COUPON_ON_EMPTY_CART,
      },
    });
  }

  couponCode.trim().toUpperCase();
  const basketId = readCookie('basketId');
  const { query, variables } = getQueryAndVariablesForApplyCouponToBasket(
    couponCode,
    basketId,
    isLoyaltyTieringEnabled,
    fromBonusPointsDay
  );
  const updateQueryFunction = getUpdateQueryFunction(isLoyaltyTieringEnabled, fromBonusPointsDay);

  return executeStatefulGraphQLQuery({
    query,
    variables,
    type: 'mutation',
    header: { fromCartPage },
    updateQuery: updateQueryFunction,
  })
    .then((res) => {
      if (res.errors) {
        errorHandler(res.errors, errorsMapping, couponCode);
      }
      if (isLoyaltyTieringEnabled && fromBonusPointsDay) {
        if (has(res.data?.applyLoyaltyBenefits, 'productItems')) {
          const priceAdjustments = res.data?.applyLoyaltyBenefits?.productItems;
          put(setLoyaltyOrderItems(priceAdjustments));
        }
        return res.data?.applyLoyaltyBenefits;
      }
      return fromBonusPointsDay ? res.data?.applyBonusDayToBasket : res.data?.applyCouponToBasket;
    })
    .catch((err) => {
      errorHandler(err, errorsMapping, couponCode);
    });
};
export const updateQueryForRemoveCoupon = (cache, data) => {
  let couponResp = {};
  if (data && data.deleteCouponFromBasket) {
    couponResp = data.deleteCouponFromBasket;
  }
  const { c_couponMap: couponMap, ...dataToUpdate } = couponResp;
  let isCacheExist = false;
  if (dataToUpdate) {
    isCacheExist = checkCache(cache);
  }
  if (isCacheExist && dataToUpdate) {
    cache.writeQuery({
      query: getCartCachedQuery,
      data: { getCart: dataToUpdate },
      // broadcast: false,
    });
  }
};

const getQueryAndVariables = (
  couponCode,
  basketId,
  isLoyaltyTieringEnabled,
  fromBonusPointsDay
) => {
  if (isLoyaltyTieringEnabled && fromBonusPointsDay) {
    return {
      query: removeLoyaltyBenefits,
      variables: {
        basketId,
        loyaltyBenefitsRequest: {
          benefits: [{ benefitType: couponCode }],
        },
      },
    };
  }
  if (fromBonusPointsDay) {
    return {
      query: removeBonusPointDay,
      variables: {},
    };
  }
  return {
    query: removeCouponOrPromoQuery,
    variables: { couponId: couponCode },
  };
};

const getUpdateQueryFunctionForRemoveCoupon = (isLoyaltyTieringEnabled, fromBonusPointsDay) => {
  if (fromBonusPointsDay) {
    if (isLoyaltyTieringEnabled) return updateQueryForRemoveLoyaltyBenefits;
    return updateQueryForBonusPoints;
  }
  return updateQueryForRemoveCoupon;
};

export const removeCouponOrPromo = (
  { couponCode = '' },
  errorsMapping,
  fromCartPage,
  fromBonusPointsDay,
  isLoyaltyTieringEnabled
) => {
  const basketId = readCookie('basketId');
  const { query, variables } = getQueryAndVariables(
    couponCode,
    basketId,
    isLoyaltyTieringEnabled,
    fromBonusPointsDay
  );
  const updateQueryFunction = getUpdateQueryFunctionForRemoveCoupon(
    isLoyaltyTieringEnabled,
    fromBonusPointsDay
  );

  return executeStatefulGraphQLQuery({
    query,
    variables,
    type: 'mutation',
    header: { fromCartPage },
    updateQuery: updateQueryFunction,
  })
    .then((res) => {
      if (res.errors) {
        errorHandler(res.errors, errorsMapping);
      }
      if (isLoyaltyTieringEnabled && fromBonusPointsDay) {
        if (has(res.data?.applyLoyaltyBenefits, 'productItems')) {
          const priceAdjustments = res.data?.applyLoyaltyBenefits?.productItems;
          put(setLoyaltyOrderItems(priceAdjustments));
        }
        return res.data.removeLoyaltyBenefits;
      }

      return !fromBonusPointsDay
        ? res.data?.deleteCouponFromBasket
        : res.data?.removeBonusDayFromBasket;
    })
    .catch((err) => {
      errorHandler(err, errorsMapping);
    });
};
const getCouponDetails = (codeTypeMap, code) => {
  return codeTypeMap.find((rcd) => (rcd?.code || rcd?.couponCode) === code);
};

export const isValidEndDate = (dateString) => {
  const date = new Date(dateString);
  // eslint-disable-next-line no-restricted-globals
  return date instanceof Date && !isNaN(date);
};

export const convertExpiryDateToPST = (endDate, couponExpiryTimeDiff) => {
  const utcDate = isValidEndDate(endDate) ? new Date(endDate) : endDate;
  if (isValidEndDate(utcDate)) {
    utcDate.setMinutes(utcDate.getMinutes() - couponExpiryTimeDiff);
  }
  const updatedDateISO = isValidEndDate(utcDate) ? utcDate.toISOString() : endDate;
  // endDate is in UTC
  const newEndData = updatedDateISO;
  let endDatepst = newEndData;
  if (newEndData) {
    const timezone = 'America/Los_Angeles';
    endDatepst = getDateObject(
      getDateObject(newEndData)?.toLocaleString('en-US', {
        timeZone: timezone,
      })
    );
  }
  return endDatepst;
};

const simulateToOffer = (
  couponList,
  codeTypeMap = [],
  isCouponOnHoldNewDesignEnabled,
  couponExpiryTimeDiff = 1,
  couponDescriptonFromPriceAdjustments = {}
) => {
  const now = getDateObject();
  const oneDay = 24 * 60 * 60 * 1000;
  return couponList.map((promo) => {
    const { code, description, legalText, isCouponApplicable } = promo;
    const {
      type = '',
      startDate = '',
      endDate = '',
      status = '',
      stamp = '',
    } = getCouponDetails(codeTypeMap, code);
    const endDatepst = convertExpiryDateToPST(endDate, couponExpiryTimeDiff);

    if (!isValidEndDate(endDatepst)) {
      logger.error('simulateToOffer error', description, endDate);
    }

    return {
      isApplied: status === 'applied',
      isOnHold:
        status === COUPON_STATUS.NO_APPLICABLE_PROMOTION && isCouponOnHoldNewDesignEnabled
          ? 'true'
          : false,
      validFrom: startDate,
      validTo: endDatepst,
      offerType: type,
      offerCode: code,
      offerText: description || couponDescriptonFromPriceAdjustments[stamp],
      offerDescription: description || couponDescriptonFromPriceAdjustments[stamp],
      legalText,
      isExpired: endDatepst
        ? Math.round((endDatepst.getTime() - now.getTime()) / oneDay) < 0
        : false,
      isCouponApplicable: isCouponApplicable === undefined || isCouponApplicable,
    };
  });
};
const getCouponList = (promoListWithType) => {
  const couponList = [];
  promoListWithType.forEach((cpn) => {
    return couponList.push(cpn.code || cpn.couponCode);
  });
  return couponList;
};

export const getPromotionDetails = (
  promoListWithType = [],
  errorsMapping,
  isCouponOnHoldNewDesignEnabled,
  couponExpiryTimeDiff,
  couponDescriptonFromPriceAdjustments
) => {
  return executeStatefulGraphQLQuery({
    query: getPromotionDetailsQuery,
    variables: {
      couponCode: getCouponList(promoListWithType),
    },
    type: 'query',
  }).then((res) => {
    let simulatedPromoList = [];
    const respData = res?.data?.getPromotionDetails?.couponDetails;
    if (respData) {
      simulatedPromoList = simulateToOffer(
        respData,
        promoListWithType,
        isCouponOnHoldNewDesignEnabled,
        couponExpiryTimeDiff,
        couponDescriptonFromPriceAdjustments
      );
    }
    return constructCouponStructure(simulatedPromoList, errorsMapping);
  });
};
export default {
  applyCouponToBasket,
  removeCouponOrPromo,
  getPromotionDetails,
};
